import { getBackendBaseUrl } from '@/lib/api/config';
import { getAuthHeaders } from '@/lib/api/auth';

function getBsiApiRoot(): string {
  const base = getBackendBaseUrl();
  return base ? `${base}/api/panel/accounting/bsi-internet-bank` : '/api/panel/accounting/bsi-internet-bank';
}

async function bsiFetch<T>(path: string, init?: RequestInit): Promise<{ success: boolean; message?: string; data: T }> {
  const res = await fetch(`${getBsiApiRoot()}${path}`, {
    ...init,
    headers: {
      ...getAuthHeaders(),
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const json = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    message?: string;
    data?: T;
  };
  if (!res.ok || json.success === false) {
    throw new Error(json.message ?? 'خطا در ارتباط با بانک صادرات');
  }
  return {
    success: true,
    message: json.message,
    data: json.data as T,
  };
}

export interface BsiIbProfileSummary {
  id: number;
  name: string;
  bank_code?: string;
  bank_name?: string;
  portal: 'corporate' | 'personal' | 'custom' | string;
  portal_label: string;
  owner_type?: 'company' | 'person' | string;
  owner_type_label?: string;
  owner_name?: string | null;
  base_url: string;
  default_username?: string | null;
  has_password?: boolean;
  notes?: string | null;
  is_default?: boolean;
  is_active_selection?: boolean;
  session_valid?: boolean;
  username_masked?: string | null;
}

export interface BsiIbStatus {
  configured: boolean;
  session_valid: boolean;
  logged_in: boolean;
  username_masked: string | null;
  auth_group: string | null;
  last_login_at: string | null;
  last_activity_at?: string | null;
  idle_timeout_minutes?: number;
  needs_otp: boolean;
  default_username: string;
  has_default_password: boolean;
  message: string | null;
  profile?: BsiIbProfileSummary | null;
  profiles?: BsiIbProfileSummary[];
}

export interface BsiIbCaptcha {
  captcha_base64: string;
  captcha_mime: string;
  captcha_suggestion?: string | null;
  ocr_available?: boolean;
}

export interface BsiIbLoginResult {
  needs_otp: boolean;
  logged_in: boolean;
  show_captcha?: boolean;
  status: BsiIbStatus;
}

export interface BsiIbConnection {
  ok: boolean;
  message: string;
  status_code?: number;
  final_url?: string | null;
}

export interface BsiIbAccountOption {
  value: string;
  label: string;
}

export interface BsiIbLastThirtyItem {
  row: number;
  date: string;
  time: string;
  branch: string;
  type: string;
  doc_no: string;
  description: string;
  credit: number;
  debit: number;
  balance: number;
}

export interface BsiStatementRegistrationSummary {
  fingerprint: string;
  registered: boolean;
  id?: number;
  project_id?: number | null;
  project_label?: string | null;
  expense_type_id?: number | null;
  expense_type_title?: string | null;
  counterparty_type?: string | null;
  counterparty_id?: number | null;
  counterparty_label?: string | null;
  notes?: string | null;
  registered_at?: string | null;
}

export interface BsiIbSummaryAccount {
  account_number: string;
  account_name: string;
  account_type: string;
  branch_code: string;
  currency_code: string;
  current_balance: number;
  effective_balance: number;
  available_balance: number;
  last_transaction_date: string;
  opening_date: string;
  status: string;
}

export interface BsiIbSummaryTotals {
  current_balance: number;
  effective_balance: number;
  available_balance: number;
  label: string;
}

export interface BsiIbDestinationAccount {
  row: number;
  branch_code: string;
  owner_name: string;
  account_number: string;
  account_label: string;
}

export interface BsiIbSyncUpdated {
  bank_account_id: number;
  account_number: string;
  name: string;
  balance_before: number;
  balance_after: number;
  direction: string;
  amount: number;
}

export interface BsiIbSyncSkipped {
  bank_account_id: number;
  account_number: string;
  name: string;
  balance: number;
}

export const bsiInternetBankApi = {
  status: () => bsiFetch<BsiIbStatus>('/status'),

  profiles: () =>
    bsiFetch<{ items: BsiIbProfileSummary[]; active_id: number }>('/profiles'),

  activateProfile: (profileId: number) =>
    bsiFetch<{ profile: BsiIbProfileSummary; status: BsiIbStatus }>(`/profiles/${profileId}/activate`, {
      method: 'POST',
    }),

  createProfile: (payload: {
    name: string;
    bank_code?: string;
    bank_name?: string;
    portal?: string;
    owner_type?: 'company' | 'person';
    owner_name?: string;
    base_url: string;
    default_username?: string;
    password?: string;
    notes?: string;
    is_default?: boolean;
  }) =>
    bsiFetch<{ id: number; items: BsiIbProfileSummary[] }>('/profiles', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateProfile: (
    profileId: number,
    payload: {
      name?: string;
      bank_code?: string;
      bank_name?: string;
      portal?: string;
      owner_type?: 'company' | 'person';
      owner_name?: string | null;
      base_url?: string;
      default_username?: string | null;
      password?: string;
      clear_password?: boolean;
      notes?: string | null;
      is_default?: boolean;
    },
  ) =>
    bsiFetch<{ id: number; items: BsiIbProfileSummary[] }>(`/profiles/${profileId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  deleteProfile: (profileId: number) =>
    bsiFetch<{ items: BsiIbProfileSummary[] }>(`/profiles/${profileId}`, {
      method: 'DELETE',
    }),

  keepalive: () =>
    bsiFetch<{ session_valid: boolean; status: BsiIbStatus }>('/keepalive', {
      method: 'POST',
    }),

  captcha: (force = false) =>
    bsiFetch<BsiIbCaptcha>(`/captcha${force ? '?force=1' : ''}`, {
      method: 'POST',
    }),

  login: (payload: {
    username?: string;
    password?: string;
    captcha: string;
    authentication_group: string;
    otp?: string;
    use_default_password?: boolean;
  }) =>
    bsiFetch<BsiIbLoginResult>('/login', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  sendSms: (payload: {
    username?: string;
    password?: string;
    captcha: string;
    authentication_group?: string;
  }) =>
    bsiFetch<{
      needs_otp: boolean;
      status: BsiIbStatus;
      captcha_base64?: string | null;
      captcha_mime?: string | null;
      captcha_suggestion?: string | null;
      ocr_available?: boolean;
    }>('/send-sms', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  connection: () => bsiFetch<BsiIbConnection>('/connection'),

  logout: () =>
    bsiFetch<BsiIbStatus>('/logout', {
      method: 'POST',
    }),

  lastThirtyAccounts: () =>
    bsiFetch<{
      accounts: BsiIbAccountOption[];
      currencies: BsiIbAccountOption[];
      fee_rial: number;
    }>('/last-thirty/accounts'),

  lastThirty: (payload: { account_number: string; currency?: string }) =>
    bsiFetch<{
      account_number: string;
      fee_rial: number;
      items: BsiIbLastThirtyItem[];
    }>('/last-thirty', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  accountSummary: (currencyCode = '00') =>
    bsiFetch<{
      currency_code: string;
      currencies: BsiIbAccountOption[];
      accounts: BsiIbSummaryAccount[];
      totals: BsiIbSummaryTotals;
    }>(`/account-summary?currency_code=${encodeURIComponent(currencyCode)}`),

  accountBalance: (payload: { account_number: string; currency?: string }) =>
    bsiFetch<{
      account_number: string;
      available_balance: number;
      current_balance: number;
      effective_balance: number;
    }>('/account-balance', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  dateToDateForm: () =>
    bsiFetch<{
      accounts: BsiIbAccountOption[];
      currencies: BsiIbAccountOption[];
      last_date: string | null;
    }>('/date-to-date/form'),

  dateToDate: (payload: {
    account_number: string;
    from_date: string;
    to_date: string;
    currency?: string;
  }) =>
    bsiFetch<{
      account_number: string;
      from_date: string;
      to_date: string;
      last_date: string | null;
      items: BsiIbLastThirtyItem[];
    }>('/date-to-date', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  destinationForm: () =>
    bsiFetch<{
      transfer_types: BsiIbAccountOption[];
    }>('/destination-accounts/form'),

  destinationAccounts: (payload: { transfer_type?: string }) =>
    bsiFetch<{
      transfer_type: string;
      items: BsiIbDestinationAccount[];
    }>('/destination-accounts', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  syncBalances: (currencyCode = '00') =>
    bsiFetch<{
      updated: BsiIbSyncUpdated[];
      skipped: BsiIbSyncSkipped[];
      unmatched: string[];
      totals: BsiIbSummaryTotals | null;
    }>('/sync-balances', {
      method: 'POST',
      body: JSON.stringify({ currency_code: currencyCode }),
    }),

  transferMenu: () =>
    bsiFetch<{
      items: BsiIbTransferMenuItem[];
    }>('/transfer/menu'),

  localTransferForm: (mode: 'own_to_other' | 'own_to_own' = 'own_to_other') =>
    bsiFetch<{
      form_path: string | null;
      mode: 'own_to_other' | 'own_to_own';
      action: string | null;
      draft_key: string | null;
      source_accounts: BsiIbAccountOption[];
      destination_accounts: BsiIbAccountOption[];
      currencies: BsiIbAccountOption[];
      reason_codes: BsiIbAccountOption[];
      fields: Record<string, string>;
      menu: BsiIbTransferMenuItem[];
    }>(`/transfer/local/form?mode=${encodeURIComponent(mode)}`),

  localTransferPreview: (payload: {
    source_account: string;
    destination_account: string;
    amount: string;
    description?: string;
    reason_code?: string;
    reason_desc?: string;
    currency?: string;
    draft_key?: string;
    mode?: 'own_to_other' | 'own_to_own';
  }) =>
    bsiFetch<{
      needs_otp: boolean;
      challenge_type: 'sms' | 'signature_password' | string | null;
      challenge_label: string | null;
      confirm_draft_key: string | null;
      step: number;
      preview: BsiIbTransferPreview | null;
      raw: string | null;
    }>('/transfer/local/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  localTransferConfirm: (payload: {
    confirm_draft_key: string;
    challenge?: string;
    otp?: string;
    signature_password?: string;
    confirm_amount?: string;
  }) =>
    bsiFetch<{
      done: boolean;
      needs_next_step: boolean;
      needs_otp: boolean;
      challenge_type: 'sms' | 'signature_password' | string | null;
      challenge_label: string | null;
      confirm_draft_key: string | null;
      step: number | null;
      reference: string | null;
      receipt_key: string | null;
      can_export_pdf: boolean;
      can_export_excel: boolean;
      needs_pending_sign?: boolean;
      pending_count?: number | null;
      signable_count?: number | null;
      raw: string | null;
    }>('/transfer/local/confirm', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  pendingTransfers: () =>
    bsiFetch<{
      items: BsiIbPendingTransfer[];
    }>('/transfer/pending'),

  pendingTransfersSummary: () =>
    bsiFetch<{
      count: number;
      signable_count: number;
    }>('/transfer/pending/summary'),

  pendingTransferSign: (payload: {
    saba_receipt: string;
    transfer_type_code: string;
    to_account?: string;
  }) =>
    bsiFetch<{
      needs_otp: boolean;
      challenge_type: 'sms' | 'signature_password' | string | null;
      challenge_label: string | null;
      confirm_draft_key: string | null;
      step: number;
      preview: BsiIbTransferPreview | null;
      raw: string | null;
    }>('/transfer/pending/sign', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  pendingTransferCancel: (payload: {
    saba_receipt: string;
    transfer_type_code: string;
    to_account?: string;
    from_account?: string;
  }) =>
    bsiFetch<{ done: boolean }>('/transfer/pending/cancel', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  interbankForm: (channel: 'satna' | 'paya' | 'pol') =>
    bsiFetch<{
      form_path: string | null;
      mode: string | null;
      channel: 'satna' | 'paya' | 'pol';
      action: string | null;
      draft_key: string | null;
      source_accounts: BsiIbAccountOption[];
      destination_ibans: BsiIbAccountOption[];
      reason_codes: BsiIbAccountOption[];
      fields: Record<string, string>;
    }>(`/transfer/interbank/form?channel=${encodeURIComponent(channel)}`),

  interbankPreview: (payload: {
    channel: 'satna' | 'paya' | 'pol';
    source_account: string;
    destination_iban?: string;
    destination_account?: string;
    destination_known_account?: string;
    amount: string;
    description?: string;
    reason_code: string;
    reason_desc?: string;
    first_name?: string;
    last_name?: string;
    corporate_name?: string;
    payment_id?: string;
    currency?: string;
    draft_key?: string;
  }) =>
    bsiFetch<{
      needs_otp: boolean;
      challenge_type: 'sms' | 'signature_password' | string | null;
      challenge_label: string | null;
      confirm_draft_key: string | null;
      step: number;
      preview: BsiIbTransferPreview | null;
      raw: string | null;
    }>('/transfer/interbank/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  inquiryIban: (payload: { account_number: string }) =>
    bsiFetch<{
      iban: string | null;
      account_name: string | null;
      account_number: string | null;
      raw: string | null;
    }>('/inquiry/iban', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  chequeStatusForm: () =>
    bsiFetch<{
      form_path: string | null;
      action: string | null;
      accounts: BsiIbAccountOption[];
      currencies: BsiIbAccountOption[];
    }>('/cheque/status/form'),

  chequeStatus: (payload: { account_no: string; cheque_no: string; currency?: string }) =>
    bsiFetch<{
      status: string | null;
      fields: Record<string, string>;
      raw: string | null;
    }>('/cheque/status', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  chequebookStatusForm: () =>
    bsiFetch<{
      form_path: string | null;
      action: string | null;
      accounts: BsiIbAccountOption[];
      currencies: BsiIbAccountOption[];
    }>('/cheque/chequebook/form'),

  chequebookStatus: (payload: { account_no: string; index_cheque_no: string; currency?: string }) =>
    bsiFetch<{
      status: string | null;
      fields: Record<string, string>;
      raw: string | null;
    }>('/cheque/chequebook', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  chequeAmountForm: () =>
    bsiFetch<{
      form_path: string | null;
      action: string | null;
      accounts: BsiIbAccountOption[];
      currencies: BsiIbAccountOption[];
    }>('/cheque/amount/form'),

  chequeAmountPreview: (payload: { account_no: string; cheque_no: string; amount: string; currency?: string }) =>
    bsiFetch<{
      done: boolean;
      needs_otp: boolean;
      challenge_type: 'sms' | 'signature_password' | string | null;
      challenge_label: string | null;
      confirm_draft_key: string | null;
      step: number | null;
      preview: BsiIbTransferPreview | null;
      status: string | null;
      fields: Record<string, string> | null;
      raw: string | null;
    }>('/cheque/amount/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  payaBatchForm: () =>
    bsiFetch<{
      draft_key: string | null;
      source_accounts: BsiIbAccountOption[];
      reason_codes: BsiIbAccountOption[];
      reason_codes_file: BsiIbAccountOption[];
      sample_links: Array<{ label: string; path: string; format: string }>;
    }>('/transfer/paya-batch/form'),

  payaBatchAddRow: (payload: {
    draft_key: string;
    source_account: string;
    destination_iban: string;
    amount: string;
    reason_code: string;
    reason_desc?: string;
    description?: string;
    first_name?: string;
    last_name?: string;
    corporate_name?: string;
    payment_id?: string;
  }) =>
    bsiFetch<{
      items: BsiIbBatchItem[];
      raw: unknown;
    }>('/transfer/paya-batch/add', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  payaBatchUpload: async (payload: {
    draft_key: string;
    reason_code: string;
    reason_desc?: string;
    file: File;
  }) => {
    const form = new FormData();
    form.append('draft_key', payload.draft_key);
    form.append('reason_code', payload.reason_code);
    if (payload.reason_desc) form.append('reason_desc', payload.reason_desc);
    form.append('file', payload.file);
    const res = await fetch(`${getBsiApiRoot()}/transfer/paya-batch/upload`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: form,
    });
    const json = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      message?: string;
      data?: { items: BsiIbBatchItem[]; file_errors?: string | null; raw?: unknown };
    };
    if (!res.ok || json.success === false) {
      throw new Error(json.message ?? 'آپلود فایل پایا دسته‌ای ناموفق بود');
    }
    return { success: true as const, message: json.message, data: json.data as { items: BsiIbBatchItem[]; file_errors?: string | null; raw?: unknown } };
  },

  payaBatchSend: (payload: { draft_key: string }) =>
    bsiFetch<{
      needs_otp: boolean;
      challenge_type: 'sms' | 'signature_password' | string | null;
      challenge_label: string | null;
      confirm_draft_key: string | null;
      step: number;
      preview: BsiIbTransferPreview | null;
      raw: string | null;
    }>('/transfer/paya-batch/send', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  payaBatchSampleUrl: (format: 'xlsx' | 'csv' | 'pdf' = 'xlsx') =>
    `${getBsiApiRoot()}/transfer/paya-batch/sample?format=${encodeURIComponent(format)}`,

  localBatchForm: () =>
    bsiFetch<{
      draft_key: string | null;
      source_accounts: BsiIbAccountOption[];
      reason_codes: BsiIbAccountOption[];
      reason_codes_file: BsiIbAccountOption[];
      sample_links: Array<{ label: string; path: string; format: string }>;
      limits: BsiIbBatchLimits | null;
    }>('/transfer/local-batch/form'),

  localBatchAddRow: (payload: {
    draft_key: string;
    source_account: string;
    destination_account: string;
    amount: string;
    reason_code: string;
    reason_desc?: string;
    description?: string;
    payment_id?: string;
  }) =>
    bsiFetch<{
      items: BsiIbBatchItem[];
      raw: unknown;
    }>('/transfer/local-batch/add', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  localBatchUpload: async (payload: {
    draft_key: string;
    reason_code: string;
    reason_desc?: string;
    file: File;
  }) => {
    const form = new FormData();
    form.append('draft_key', payload.draft_key);
    form.append('reason_code', payload.reason_code);
    if (payload.reason_desc) form.append('reason_desc', payload.reason_desc);
    form.append('file', payload.file);
    const res = await fetch(`${getBsiApiRoot()}/transfer/local-batch/upload`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
      body: form,
    });
    const json = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      message?: string;
      data?: { items: BsiIbBatchItem[]; file_errors?: string | null; raw?: unknown };
    };
    if (!res.ok || json.success === false) {
      throw new Error(json.message ?? 'آپلود فایل دسته صادراتی ناموفق بود');
    }
    return { success: true as const, message: json.message, data: json.data as { items: BsiIbBatchItem[]; file_errors?: string | null; raw?: unknown } };
  },

  localBatchSend: (payload: { draft_key: string }) =>
    bsiFetch<{
      needs_otp: boolean;
      challenge_type: 'sms' | 'signature_password' | string | null;
      challenge_label: string | null;
      confirm_draft_key: string | null;
      step: number;
      preview: BsiIbTransferPreview | null;
      raw: string | null;
    }>('/transfer/local-batch/send', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  localBatchSampleUrl: (format: 'xlsx' | 'csv' | 'pdf' = 'xlsx') =>
    `${getBsiApiRoot()}/transfer/local-batch/sample?format=${encodeURIComponent(format)}`,

  transferReceiptLookup: (payload: { track_number: string }) =>
    bsiFetch<{
      reference: string | null;
      receipt_key: string | null;
      fields: Record<string, string>;
      can_export_pdf: boolean;
      can_export_excel: boolean;
    }>('/transfer/receipt', {
      method: 'POST',
      body: JSON.stringify({ ...payload, export: 'html' }),
    }),

  receiptDownloadUrl: (receiptKey: string, format: 'html' | 'pdf' | 'excel' = 'html') =>
    `${getBsiApiRoot()}/transfer/receipt/download?receipt_key=${encodeURIComponent(receiptKey)}&format=${encodeURIComponent(format)}`,

  transferReceiptExportUrl: (trackNumber: string, exportType: 'pdf' | 'excel') =>
    `${getBsiApiRoot()}/transfer/receipt`,

  lookupStatementRegistrations: (payload: { account_number: string; items: BsiIbLastThirtyItem[] }) =>
    bsiFetch<{ items: BsiStatementRegistrationSummary[] }>('/statement-registrations/lookup', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  storeStatementRegistration: (payload: {
    account_number: string;
    report_source: 'last_thirty' | 'date_to_date';
    item: BsiIbLastThirtyItem;
    project_id?: number | null;
    expense_type_id?: number | null;
    beneficiary?: string;
    notes?: string;
  }) =>
    bsiFetch<BsiStatementRegistrationSummary>('/statement-registrations', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateStatementRegistrationExpenseType: (id: number, expenseTypeId: number | null) =>
    bsiFetch<BsiStatementRegistrationSummary>(`/statement-registrations/${id}/expense-type`, {
      method: 'PATCH',
      body: JSON.stringify({ expense_type_id: expenseTypeId }),
    }),
};

export interface BsiIbBatchItem {
  source_account: string;
  destination_iban: string;
  destination_account?: string;
  amount: number;
  description: string;
  first_name: string;
  last_name: string;
  corporate_name: string;
  payment_id?: string;
}

export interface BsiIbBatchLimits {
  max_rows_per_batch: number;
  max_amount_per_tx: number;
  max_amount_per_day: number;
  daily_count_limit: number | null;
  fee_rial: number;
  notes: string[];
}

export interface BsiIbPendingTransfer {
  saba_receipt: string;
  transfer_type_code: string;
  transfer_type: string;
  from_account: string;
  from_name?: string;
  to_account: string;
  to_name?: string;
  to_iban?: string;
  to_bank_name?: string;
  amount: number;
  amount_display: string;
  create_date: string;
  description: string;
  reason_code: string;
  reason_code_desc: string;
  reason_desc: string;
  pending_status: string;
  pending_status_code: number | null;
  can_sign: boolean;
  can_cancel?: boolean;
  sign_supported: boolean;
}

export interface BsiIbTransferMenuItem {
  path: string;
  label: string;
}

export interface BsiIbTransferPreview {
  source_account: string;
  destination_account: string;
  amount: number;
  currency: string;
  description: string;
  channel?: 'satna' | 'paya' | 'pol' | string;
  reason_code?: string;
  reason_desc?: string;
  saba_receipt?: string;
  transfer_type_code?: string;
  form_path: string;
  action: string;
  mode?: 'own_to_other' | 'own_to_own' | 'pending_sign' | string;
}
